import type { ExtractionStatus } from '../network/protocol';

export interface ExtractionChoiceProjection {
  bankAvailable: boolean;
  hasNextTrain: boolean;
  canAdvance: boolean;
  advanceOpensIn: number;
  bankValue: number;
  currentMultiplier: number;
  riskValue: number;
  nextMultiplier: number;
}

export function projectExtractionChoice(status: ExtractionStatus, carriedValue: number): ExtractionChoiceProjection {
  const value = Number.isFinite(carriedValue) ? Math.max(0, carriedValue) : 0;
  const currentMultiplier = Number.isFinite(status.multiplier) ? Math.max(1, status.multiplier) : 1;
  const maximumTrain = Number.isFinite(status.maximumTrain) ? Math.max(1, Math.trunc(status.maximumTrain)) : 5;
  const hasNextTrain = status.stopNumber < maximumTrain;
  const advanceOpensIn = Number.isFinite(status.nextTrainOpensIn)
    ? Math.max(0, status.nextTrainOpensIn ?? 0)
    : 0;
  // Missing state remains open during a rolling frontend/backend deployment.
  const canAdvance = hasNextTrain && status.nextTrainState !== 'approaching';
  const riskMultiplier = hasNextTrain ? nextMultiplier(status.stopNumber) : currentMultiplier;
  return {
    bankAvailable: status.state === 'open',
    hasNextTrain,
    canAdvance,
    advanceOpensIn,
    bankValue: Math.round(value*currentMultiplier),
    currentMultiplier,
    riskValue: Math.round(value*riskMultiplier),
    nextMultiplier: riskMultiplier,
  };
}

/** The route banner and cab choice must describe the same authoritative state. */
export function extractionObjective(status: ExtractionStatus, insideZone: boolean, progress: number): string {
  if (status.state === 'ended' || status.state === 'departed') return 'EXTRACTION CLOSED';
  if (!insideZone) return 'REACH LOCOMOTIVE';
  const choice = projectExtractionChoice(status, 0);
  if (choice.bankAvailable) {
    if (progress > 0) return 'BANKING // HOLD X';
    if (!choice.hasNextTrain) return 'BANK YOUR HAUL';
    return choice.canAdvance ? 'BANK OR RISK MORE' : 'BANK OR HOLD';
  }
  return choice.canAdvance ? 'ADVANCE OR HOLD' : 'HOLD THE CAB';
}

export function nextMultiplier(trainNumber: number): number {
  if (trainNumber <= 1) return 1.35;
  if (trainNumber === 2) return 1.75;
  if (trainNumber === 3) return 2.20;
  return Math.min(4, Math.round((2.20+(trainNumber-3)*0.35)*100)/100);
}
